import {
	Box,
	Button,
	Flex,
	Heading,
	Input,
	Link,
	Stack,
	Text,
	InputRightElement,
	InputGroup
} from '@chakra-ui/react';
import { FormEvent, useState } from 'react';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { CampoForm } from '../../components/CampoForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { emailInvalido, vazio } from '../../utils/validacao';
import Bg from '../../assets/images/background.png';
import useAuth from '../../hooks/useAuth';

type Campo = 'username' | 'password';

export default function Login() {
	const [ username, setUsername ] = useState('');
	const [ password, setPassword ] = useState('');
	const [ isLoading, setIsLoading ] = useState(false);
	const { erros, validar, limparErro } = useErrosForm<Campo>();

	const navigate = useNavigate();

	const { login } = useAuth();

	const [ show, setShow ] = useState(false);
	const handleClick = () => setShow(!show);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(username)) {
			encontrados.username = 'Email é obrigatório!';
		} else if (emailInvalido(username)) {
			encontrados.username = 'Informe um email válido.';
		}

		if (!password) encontrados.password = 'Senha é obrigatória!';

		return encontrados;
	};

	// FormEvent sem o generico porque o <form> vem do `as` do Stack, que mantem a
	// tipagem de div do Chakra.
	const handleLogin = async (event: FormEvent) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setIsLoading(true);
		login({ username: username.trim(), password }).then((res) => {
			if (!res) {
				setIsLoading(false);
			}
		});
	};

	return (
		<Flex
			minH={'100vh'}
			align={'center'}
			justify={'center'}
			bgImage={Bg}
			bgSize={'cover'}
			bgPosition={'center'}
			position={'relative'}
			p={6}
		>
			{/* navy wash over the crest background so the card always reads */}
			<Box
				position={'absolute'}
				inset={0}
				bgGradient={'linear(150deg, rgba(14,31,56,0.94), rgba(16,38,74,0.82))'}
			/>
			{/* the "current": steel diagonals echoing the mane and logo stripes */}
			<Box
				position={'absolute'}
				inset={0}
				opacity={0.35}
				pointerEvents={'none'}
				bgImage={
					'repeating-linear-gradient(115deg, transparent 0 46px, rgba(124,153,196,0.5) 46px 48px)'
				}
			/>
			<Stack
				as={'form'}
				onSubmit={handleLogin}
				noValidate
				position={'relative'}
				spacing={5}
				w={'full'}
				maxW={'md'}
				bg={'rgba(11,26,49,0.72)'}
				borderWidth={'1px'}
				borderColor={'whiteAlpha.200'}
				backdropFilter={'blur(6px)'}
				padding={'40px'}
				borderRadius={'18px'}
				boxShadow={'0 30px 60px -30px rgba(0,0,0,0.75)'}
			>
				<Box borderTopWidth={'3px'} borderTopColor={'brass.500'} pt={4} mt={-2}>
					<Heading
						color={'white'}
						fontSize={'5xl'}
						textTransform={'uppercase'}
						lineHeight={1}
					>
						Windfall
					</Heading>
					<Text
						color={'brass.500'}
						fontSize={'sm'}
						fontWeight={600}
						letterSpacing={'0.28em'}
						textTransform={'uppercase'}
						mt={2}
					>
						Language Institute
					</Text>
				</Box>

				<CampoForm
					label="Seu email"
					htmlFor="email"
					erro={erros.username}
					labelColor="whiteAlpha.800"
					ocultarAsterisco
				>
					<Input
						id="email"
						type="email"
						autoComplete="email"
						placeholder="seuemail@seuemail.com"
						value={username}
						onChange={(e) => {
							setUsername(e.target.value);
							limparErro('username');
						}}
						disabled={isLoading}
						color="white"
						bg={'whiteAlpha.100'}
						borderColor={'whiteAlpha.300'}
						_placeholder={{ color: 'whiteAlpha.500' }}
					/>
				</CampoForm>

				<CampoForm
					label="Senha"
					htmlFor="senha"
					erro={erros.password}
					labelColor="whiteAlpha.800"
					ocultarAsterisco
				>
					<InputGroup size="md">
						<Input
							id="senha"
							type={show ? 'text' : 'password'}
							autoComplete="current-password"
							placeholder="*********"
							value={password}
							onChange={(e) => {
								setPassword(e.target.value);
								limparErro('password');
							}}
							disabled={isLoading}
							color="white"
							bg={'whiteAlpha.100'}
							borderColor={'whiteAlpha.300'}
							_placeholder={{ color: 'whiteAlpha.500' }}
						/>
						<InputRightElement width="4.5rem">
							<Button
								type="button"
								h="1.75rem"
								size="sm"
								onClick={handleClick}
								aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
								variant={'unstyled'}
								color={'whiteAlpha.800'}
								display={'flex'}
								alignItems={'center'}
								justifyContent={'center'}
							>
								{show ? <AiFillEyeInvisible size={'25'} /> : <AiFillEye size={'25'} />}
							</Button>
						</InputRightElement>
					</InputGroup>
				</CampoForm>

				<Stack spacing={5}>
					<Link
						color={'whiteAlpha.800'}
						fontSize={'sm'}
						alignSelf={'flex-start'}
						_hover={{ color: 'white' }}
						onClick={() => navigate('/forgot-password')}
					>
						Esqueceu sua senha?
					</Link>
					<Button colorScheme={'blue'} variant={'solid'} type="submit" isLoading={isLoading}>
						Entrar
					</Button>
				</Stack>
			</Stack>
		</Flex>
	);
}
